import { ethers } from 'ethers';
import { NetworkInfo, NetworkServiceResult } from '../services/networkService';
import { MetaMaskError, MetaMaskErrorType, parseMetaMaskError } from './metamask';
import { RetryableError, withRetry, RetryOptions } from './errorHandling';

export interface NetworkErrorContext {
  operation: string;
  chainId?: string;
  networkName?: string;
  rpcUrl?: string;
  attemptCount?: number;
}

export interface NetworkRecoveryOptions {
  maxRetries: number;
  baseDelay: number;
  enableFallbackRpc: boolean;
  showUserGuidance: boolean;
}

export const defaultNetworkRecoveryOptions: NetworkRecoveryOptions = {
  maxRetries: 3,
  baseDelay: 2000,
  enableFallbackRpc: true,
  showUserGuidance: true,
};

export class NetworkError extends Error {
  public readonly code: string;
  public readonly context: NetworkErrorContext;
  public readonly canRetry: boolean;
  public readonly userGuidance?: string;
  public readonly recoveryActions?: string[];

  constructor(
    message: string,
    code: string,
    context: NetworkErrorContext,
    canRetry = true,
    userGuidance?: string,
    recoveryActions?: string[]
  ) {
    super(message);
    this.name = 'NetworkError';
    this.code = code;
    this.context = context;
    this.canRetry = canRetry;
    this.userGuidance = userGuidance;
    this.recoveryActions = recoveryActions;
  }
}

/**
 * Enhanced MetaMask connectivity checker with exponential backoff
 */
export class MetaMaskConnectivityChecker {
  private static instance: MetaMaskConnectivityChecker;
  private provider: ethers.BrowserProvider | null = null;
  private lastConnectivityCheck = 0;
  private connectivityCacheMs = 5000; // Cache connectivity status for 5 seconds

  static getInstance(): MetaMaskConnectivityChecker {
    if (!MetaMaskConnectivityChecker.instance) {
      MetaMaskConnectivityChecker.instance = new MetaMaskConnectivityChecker();
    }
    return MetaMaskConnectivityChecker.instance;
  }

  private async initializeProvider(): Promise<void> {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);
    }
  }

  async checkConnectivity(options: Partial<NetworkRecoveryOptions> = {}): Promise<boolean> {
    const config = { ...defaultNetworkRecoveryOptions, ...options };
    const now = Date.now();

    // Use cached result if recent
    if (now - this.lastConnectivityCheck < this.connectivityCacheMs) {
      return this.provider !== null;
    }

    try {
      await this.initializeProvider();
      
      if (!this.provider) {
        throw new NetworkError(
          'MetaMask provider not available',
          'PROVIDER_NOT_AVAILABLE',
          { operation: 'connectivity_check' },
          false,
          'Please install MetaMask extension and refresh the page',
          ['Install MetaMask from metamask.io', 'Refresh the page', 'Try again']
        );
      }

      // Test basic connectivity with retry
      await withRetry(async () => {
        const network = await this.provider!.getNetwork();
        if (!network) {
          throw new RetryableError('Failed to get network information', true, 'NETWORK_INFO_FAILED');
        }
      }, {
        maxAttempts: config.maxRetries,
        baseDelay: config.baseDelay,
        maxDelay: 10000,
        backoffFactor: 2
      });

      this.lastConnectivityCheck = now;
      return true;
    } catch (error) {
      console.error('MetaMask connectivity check failed:', error);
      this.provider = null;
      this.lastConnectivityCheck = now;
      return false;
    }
  }

  async testRpcEndpoint(rpcUrl: string, timeout = 5000): Promise<boolean> {
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      
      // Test with timeout
      const networkPromise = provider.getNetwork();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('RPC timeout')), timeout)
      );

      await Promise.race([networkPromise, timeoutPromise]);
      return true;
    } catch (error) {
      console.warn(`RPC endpoint ${rpcUrl} failed:`, error);
      return false;
    }
  }

  getProvider(): ethers.BrowserProvider | null {
    return this.provider;
  }
}

/**
 * Network switching error handler with user guidance
 */
export class NetworkSwitchingHandler {
  private static readonly COMMON_ERRORS = {
    USER_REJECTED: 4001,
    NETWORK_NOT_ADDED: 4902,
    UNAUTHORIZED: 4100,
    UNSUPPORTED_METHOD: 4200,
    DISCONNECTED: 4900,
    CHAIN_DISCONNECTED: 4901,
  };

  static async handleNetworkSwitchError(
    error: unknown,
    targetNetwork: NetworkInfo,
    context: NetworkErrorContext
  ): Promise<NetworkError> {
    const metaMaskError = parseMetaMaskError(error);
    
    switch (metaMaskError.code) {
      case NetworkSwitchingHandler.COMMON_ERRORS.USER_REJECTED:
        return new NetworkError(
          'Network switch was rejected by user',
          'USER_REJECTED_NETWORK_SWITCH',
          { ...context, networkName: targetNetwork.name },
          true,
          'Please approve the network switch in MetaMask to continue',
          [
            'Click "Switch Network" again',
            'Approve the network change in MetaMask popup',
            'If popup doesn\'t appear, check MetaMask extension'
          ]
        );

      case NetworkSwitchingHandler.COMMON_ERRORS.NETWORK_NOT_ADDED:
        return new NetworkError(
          'Network not found in MetaMask',
          'NETWORK_NOT_ADDED',
          { ...context, networkName: targetNetwork.name },
          true,
          'The network needs to be added to MetaMask first',
          [
            'Click "Add Network" to add it to MetaMask',
            'Approve adding the network in MetaMask popup',
            'Then try switching again'
          ]
        );

      case NetworkSwitchingHandler.COMMON_ERRORS.UNAUTHORIZED:
        return new NetworkError(
          'MetaMask connection unauthorized',
          'METAMASK_UNAUTHORIZED',
          { ...context, networkName: targetNetwork.name },
          true,
          'Please connect your MetaMask wallet first',
          [
            'Click "Connect Wallet" to connect MetaMask',
            'Approve the connection in MetaMask popup',
            'Then try switching networks again'
          ]
        );

      case NetworkSwitchingHandler.COMMON_ERRORS.DISCONNECTED:
      case NetworkSwitchingHandler.COMMON_ERRORS.CHAIN_DISCONNECTED:
        return new NetworkError(
          'MetaMask is disconnected',
          'METAMASK_DISCONNECTED',
          { ...context, networkName: targetNetwork.name },
          true,
          'MetaMask connection was lost',
          [
            'Check your internet connection',
            'Refresh the page',
            'Reconnect MetaMask if needed'
          ]
        );

      default:
        return new NetworkError(
          `Network switch failed: ${metaMaskError.message}`,
          'NETWORK_SWITCH_FAILED',
          { ...context, networkName: targetNetwork.name },
          true,
          'An unexpected error occurred while switching networks',
          [
            'Try switching networks manually in MetaMask',
            'Refresh the page and try again',
            'Check MetaMask for any pending requests'
          ]
        );
    }
  }

  static async handleNetworkAddError(
    error: unknown,
    targetNetwork: NetworkInfo,
    context: NetworkErrorContext
  ): Promise<NetworkError> {
    const metaMaskError = parseMetaMaskError(error);
    
    switch (metaMaskError.code) {
      case NetworkSwitchingHandler.COMMON_ERRORS.USER_REJECTED:
        return new NetworkError(
          'Adding network was rejected by user',
          'USER_REJECTED_ADD_NETWORK',
          { ...context, networkName: targetNetwork.name },
          true,
          'Please approve adding the network to MetaMask',
          [
            'Click "Add Network" again',
            'Approve adding the network in MetaMask popup',
            'Make sure you trust the network configuration'
          ]
        );

      default:
        return new NetworkError(
          `Failed to add network: ${metaMaskError.message}`,
          'ADD_NETWORK_FAILED',
          { ...context, networkName: targetNetwork.name },
          true,
          'Could not add the network to MetaMask',
          [
            'Try adding the network manually in MetaMask settings',
            'Check the network configuration details',
            'Ensure MetaMask is up to date'
          ]
        );
    }
  }
}

/**
 * RPC endpoint manager with fallback support
 */
export class RpcEndpointManager {
  private static readonly SWARM_DEVNET_ENDPOINTS = [
    'https://rpc.devnet.swarmind.ai',
    'https://rpc-backup.devnet.swarmind.ai', // Fallback endpoint
  ];

  private static readonly LOCAL_ENDPOINTS = [
    'http://127.0.0.1:8545',
    'http://localhost:8545',
    'http://127.0.0.1:7545', // Ganache default
  ];

  static async findWorkingEndpoint(
    endpoints: string[],
    timeout = 5000
  ): Promise<string | null> {
    const checker = MetaMaskConnectivityChecker.getInstance();
    
    for (const endpoint of endpoints) {
      const isWorking = await checker.testRpcEndpoint(endpoint, timeout);
      if (isWorking) {
        return endpoint;
      }
    }
    
    return null;
  }

  static async getSwarmDevnetEndpoint(): Promise<string> {
    const workingEndpoint = await this.findWorkingEndpoint(this.SWARM_DEVNET_ENDPOINTS);
    
    if (!workingEndpoint) {
      throw new NetworkError(
        'All Swarm devnet RPC endpoints are unavailable',
        'ALL_RPC_ENDPOINTS_FAILED',
        { operation: 'get_swarm_endpoint' },
        true,
        'Swarm devnet is currently unavailable',
        [
          'Check your internet connection',
          'Try again in a few minutes',
          'Contact support if the issue persists'
        ]
      );
    }
    
    return workingEndpoint;
  }

  static async getLocalEndpoint(): Promise<string> {
    const workingEndpoint = await this.findWorkingEndpoint(this.LOCAL_ENDPOINTS);
    
    if (!workingEndpoint) {
      throw new NetworkError(
        'No local development network found',
        'LOCAL_NETWORK_NOT_FOUND',
        { operation: 'get_local_endpoint' },
        false,
        'Local development network is not running',
        [
          'Start your local blockchain (Hardhat, Ganache, etc.)',
          'Check that it\'s running on port 8545',
          'Verify your local network configuration'
        ]
      );
    }
    
    return workingEndpoint;
  }
}

/**
 * Manual network addition instructions generator
 */
export class ManualNetworkInstructions {
  static generateSwarmDevnetInstructions(): string[] {
    return [
      '1. Open MetaMask extension',
      '2. Click on the network dropdown (top of MetaMask)',
      '3. Click "Add Network" at the bottom',
      '4. Click "Add a network manually"',
      '5. Enter the following details:',
      '   • Network Name: Swarm',
      '   • New RPC URL: https://rpc.devnet.swarmind.ai',
      '   • Chain ID: 1322837',
      '   • Currency Symbol: ETH',
      '   • Block Explorer: https://explorer.devnet.swarmind.ai/',
      '6. Click "Save"',
      '7. Switch to the Swarm network',
      '8. Refresh this page and try again'
    ];
  }

  static generateLocalNetworkInstructions(rpcUrl = 'http://127.0.0.1:8545'): string[] {
    return [
      '1. Make sure your local blockchain is running',
      '2. Open MetaMask extension',
      '3. Click on the network dropdown',
      '4. Click "Add Network" → "Add a network manually"',
      '5. Enter the following details:',
      `   • Network Name: Local Development`,
      `   • New RPC URL: ${rpcUrl}`,
      '   • Chain ID: 1337 (or your local chain ID)',
      '   • Currency Symbol: ETH',
      '6. Click "Save" and switch to the network',
      '7. Refresh this page and try again'
    ];
  }

  static generateTroubleshootingSteps(): string[] {
    return [
      'Common troubleshooting steps:',
      '• Refresh the page and try again',
      '• Check that MetaMask is unlocked',
      '• Ensure you have a stable internet connection',
      '• Try disabling other browser extensions temporarily',
      '• Clear browser cache and cookies for this site',
      '• Update MetaMask to the latest version',
      '• Restart your browser',
      'If issues persist, contact support with error details'
    ];
  }
}

/**
 * Network error recovery orchestrator
 */
export class NetworkErrorRecovery {
  static async recoverFromNetworkError(
    error: NetworkError,
    options: Partial<NetworkRecoveryOptions> = {}
  ): Promise<NetworkServiceResult> {
    const config = { ...defaultNetworkRecoveryOptions, ...options };
    
    console.log(`Attempting recovery from network error: ${error.code}`, error.context);
    
    switch (error.code) {
      case 'PROVIDER_NOT_AVAILABLE':
        return {
          success: false,
          error: error.message,
          userGuidance: error.userGuidance,
          recoveryActions: error.recoveryActions
        };

      case 'USER_REJECTED_NETWORK_SWITCH':
      case 'USER_REJECTED_ADD_NETWORK':
        return {
          success: false,
          error: error.message,
          userGuidance: error.userGuidance,
          recoveryActions: error.recoveryActions,
          canRetry: true
        };

      case 'ALL_RPC_ENDPOINTS_FAILED':
        if (config.enableFallbackRpc) {
          try {
            const fallbackEndpoint = await RpcEndpointManager.getSwarmDevnetEndpoint();
            console.log(`Using fallback RPC endpoint: ${fallbackEndpoint}`);
            return { success: true };
          } catch (fallbackError) {
            return {
              success: false,
              error: 'All network endpoints are unavailable',
              userGuidance: 'Network connectivity issues detected',
              recoveryActions: ManualNetworkInstructions.generateTroubleshootingSteps()
            };
          }
        }
        break;

      case 'NETWORK_NOT_ADDED':
        return {
          success: false,
          error: error.message,
          userGuidance: 'Network needs to be added to MetaMask',
          recoveryActions: ManualNetworkInstructions.generateSwarmDevnetInstructions(),
          requiresManualAction: true
        };

      case 'LOCAL_NETWORK_NOT_FOUND':
        return {
          success: false,
          error: error.message,
          userGuidance: error.userGuidance,
          recoveryActions: ManualNetworkInstructions.generateLocalNetworkInstructions(),
          requiresManualAction: true
        };
    }

    // Default recovery attempt with retry
    if (error.canRetry && config.maxRetries > 0) {
      return {
        success: false,
        error: error.message,
        userGuidance: error.userGuidance,
        recoveryActions: error.recoveryActions,
        canRetry: true
      };
    }

    return {
      success: false,
      error: error.message,
      userGuidance: error.userGuidance || 'An unexpected network error occurred',
      recoveryActions: error.recoveryActions || ManualNetworkInstructions.generateTroubleshootingSteps()
    };
  }
}

// Extend NetworkServiceResult to include recovery information
declare module '../services/networkService' {
  interface NetworkServiceResult {
    userGuidance?: string;
    recoveryActions?: string[];
    canRetry?: boolean;
    requiresManualAction?: boolean;
  }
}